import {
  IconBell,
  IconCalendarEvent,
  IconCamera,
  IconCar,
  IconCards,
  IconClipboardList,
  IconCoins,
  IconDashboard,
  IconReportMoney,
  IconDatabase,
  IconFlag,
  IconMap,
  IconMedal,
  IconNut,
  IconFolder,
  IconShield,
  IconShieldLock,
  IconSpeakerphone,
  IconUsers,
  IconUsersGroup,
  type Icon,
} from "@tabler/icons-react";

import type { Area } from "@/lib/permissions";
import { AnnouncementsPage } from "@/pages/dashboard/announcements";
import { BadgesPage } from "@/pages/dashboard/badges";
import { ClubsPage } from "@/pages/dashboard/clubs";
import { ConfigsPage } from "@/pages/dashboard/configs";
import { CostsPage } from "@/pages/dashboard/costs";
import { CountersPage } from "@/pages/dashboard/counters";
import { CreditsPage } from "@/pages/dashboard/credits";
import { EventsPage } from "@/pages/dashboard/events";
import { FeatureFlagsPage } from "@/pages/dashboard/feature-flags";
import { DashboardPage } from "@/pages/dashboard/index";
import { LiveMapPage } from "@/pages/dashboard/live-map";
import { NotificationsPage } from "@/pages/dashboard/notifications";
import { PhotoSpotsPage } from "@/pages/dashboard/photo-spots";
import { SpecialUsersPage } from "@/pages/dashboard/special-users";
import { StoragePage } from "@/pages/dashboard/storage";
import { UserSafetyPage } from "@/pages/dashboard/user-safety";
import { UserCardsPage } from "@/pages/dashboard/user_cards";
import { UsersPage } from "@/pages/dashboard/users";
import { VehiclesPage } from "@/pages/dashboard/vehicles";
import { WaitlistPage } from "@/pages/dashboard/waitlist";

// One table drives the router, the sidebar and the permission gate, so a page
// can never be routable without also declaring what it takes to open it.
// Paths mirror the backend area names; `area: null` marks the few screens that
// only call public /v1 routes and therefore aren't gated.
export interface DashboardRoute {
  /** Path segment under /dashboard. `undefined` marks the index route. */
  path?: string;
  area: Area | null;
  title: string;
  icon: Icon;
  group: "main" | "community" | "backoffice";
  element: React.ReactNode;
}

export const DASHBOARD_ROUTES: DashboardRoute[] = [
  {
    area: "admin/users",
    title: "Dashboard",
    icon: IconDashboard,
    group: "main",
    element: <DashboardPage />,
  },
  {
    path: "costs",
    area: "admin/costs",
    title: "Costs",
    icon: IconReportMoney,
    group: "main",
    element: <CostsPage />,
  },
  {
    path: "live-map",
    area: null,
    title: "Live Map",
    icon: IconMap,
    group: "main",
    element: <LiveMapPage />,
  },
  {
    path: "photo-spots",
    area: null,
    title: "Photo Spots",
    icon: IconCamera,
    group: "community",
    element: <PhotoSpotsPage />,
  },
  {
    path: "users",
    area: "admin/users",
    title: "Users",
    icon: IconUsers,
    group: "community",
    element: <UsersPage />,
  },
  {
    path: "events",
    area: "admin/events",
    title: "Events",
    icon: IconCalendarEvent,
    group: "community",
    element: <EventsPage />,
  },
  {
    path: "vehicles",
    area: null,
    title: "Vehicles",
    icon: IconCar,
    group: "community",
    element: <VehiclesPage />,
  },
  {
    path: "cards",
    area: "admin/cards",
    title: "Profile Cards",
    icon: IconCards,
    group: "community",
    element: <UserCardsPage />,
  },
  {
    path: "badges",
    area: "admin/badges",
    title: "Badges",
    icon: IconMedal,
    group: "community",
    element: <BadgesPage />,
  },
  {
    path: "clubs",
    area: "admin/clubs",
    title: "Clubs",
    icon: IconUsersGroup,
    group: "community",
    element: <ClubsPage />,
  },
  {
    path: "announcements",
    area: "admin/announcements",
    title: "Announcements",
    icon: IconSpeakerphone,
    group: "community",
    element: <AnnouncementsPage />,
  },
  {
    path: "features",
    area: "admin/features",
    title: "Feature Flags",
    icon: IconFlag,
    group: "backoffice",
    element: <FeatureFlagsPage />,
  },
  {
    path: "configs",
    area: "admin/configs",
    title: "Configs",
    icon: IconNut,
    group: "backoffice",
    element: <ConfigsPage />,
  },
  {
    path: "safety",
    area: "admin/safety",
    title: "User Safety",
    icon: IconShield,
    group: "main",
    element: <UserSafetyPage />,
  },
  {
    path: "counters",
    area: "admin/counters",
    title: "Counters",
    icon: IconDatabase,
    group: "backoffice",
    element: <CountersPage />,
  },
  {
    path: "notifications",
    area: "admin/users",
    title: "Notifications",
    icon: IconBell,
    group: "backoffice",
    element: <NotificationsPage />,
  },
  {
    path: "credits",
    area: "admin/credits",
    title: "Credits",
    icon: IconCoins,
    group: "community",
    element: <CreditsPage />,
  },
  {
    path: "waitlist",
    area: "admin/waitlist",
    title: "Waitlist",
    icon: IconClipboardList,
    group: "main",
    element: <WaitlistPage />,
  },
  {
    path: "special-users",
    area: "admin/special-users",
    title: "Special Users",
    icon: IconShieldLock,
    group: "backoffice",
    element: <SpecialUsersPage />,
  },
  {
    path: "storage",
    area: "admin/storage",
    title: "Storage",
    icon: IconFolder,
    group: "backoffice",
    element: <StoragePage />,
  },
];

export const routeUrl = (route: DashboardRoute) =>
  route.path ? `/dashboard/${route.path}` : "/dashboard";
